import React, { useCallback, useEffect, useState } from "react";
import { api, uploadMedia } from "@/api/cms";

const labels = {
  pages: "Pages",
  products: "Products",
  blog: "Blog posts",
};

export const Dashboard = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    api("/admin/dashboard").then(setData).catch(() => setData(null));
  }, []);
  return (
    <div>
      <h1 className="font-display text-3xl">Dashboard</h1>
      <div className="mt-6 grid gap-4 md:grid-cols-3">
        {Object.entries(data?.counts || {}).map(([key, value]) => (
          <div key={key} className="paper p-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-faint">{key}</p>
            <p className="mt-2 text-3xl">{value.total}</p>
            <p className="text-sm text-ink-soft">{value.published} published · {value.draft} draft</p>
          </div>
        ))}
      </div>
      <h2 className="font-display text-xl mt-8">Recent edits</h2>
      <div className="mt-3 paper divide-y divide-[var(--paper-edge)]">
        {(data?.recent_edits || []).map((item) => (
          <p key={item.id} className="p-3 text-sm">{item.action} · {item.collection} · {item.created_at}</p>
        ))}
      </div>
    </div>
  );
};

export const ContentManager = ({ type }) => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(() => api(`/admin/${type}`).then(setItems).catch((err) => setError(err.message)), [type]);
  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="flex items-center justify-between gap-3">
        <h1 className="font-display text-3xl">{labels[type]}</h1>
        <button onClick={() => setEditing({ title: "", slug: "", status: "draft", content: "", images: [] })} className="bg-ink text-paper px-4 py-2 text-sm">New</button>
      </div>
      {error && <p className="mt-3 text-accentRed">{error}</p>}
      {editing && (
        <ContentForm
          type={type}
          item={editing}
          onCancel={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      <div className="mt-6 paper divide-y divide-[var(--paper-edge)]">
        {items.map((item) => (
          <div key={item.id} className="p-4 flex items-center justify-between gap-4">
            <div>
              <h2 className="font-display text-lg">{item.title}</h2>
              <p className="text-sm text-ink-soft">{item.slug} · {item.status} · updated {item.updated_at}</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setEditing(item)} className="border border-ink px-3 py-1 text-sm">Edit</button>
              <button
                onClick={async () => {
                  if (!window.confirm(`Delete ${item.title}?`)) return;
                  await api(`/admin/${type}/${item.id}`, { method: "DELETE" });
                  load();
                }}
                className="text-accentRed px-3 py-1 text-sm"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ContentForm = ({ type, item, onCancel, onSaved }) => {
  const [form, setForm] = useState(item);
  const [preview, setPreview] = useState(false);
  const [error, setError] = useState("");

  const set = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));

  const save = async () => {
    setError("");
    try {
      const payload = { title: form.title, slug: form.slug, status: form.status, content: form.content, images: form.images || [] };
      if (form.id) await api(`/admin/${type}/${form.id}`, { method: "PATCH", body: JSON.stringify(payload) });
      else await api(`/admin/${type}`, { method: "POST", body: JSON.stringify(payload) });
      onSaved();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="mt-6 paper p-5">
      <div className="grid md:grid-cols-2 gap-4">
        <label className="text-sm">Title<input className="mt-1 w-full border p-2 bg-transparent" value={form.title || ""} onChange={set("title")} /></label>
        <label className="text-sm">Slug<input className="mt-1 w-full border p-2 bg-transparent" value={form.slug || ""} onChange={set("slug")} /></label>
        <label className="text-sm">Status<select className="mt-1 w-full border p-2 bg-transparent" value={form.status || "draft"} onChange={set("status")}><option value="draft">Draft</option><option value="published">Published</option></select></label>
        <label className="text-sm">Upload image<input className="mt-1 w-full" type="file" accept="image/*" onChange={async (e) => {
          if (!e.target.files?.[0]) return;
          const media = await uploadMedia(e.target.files[0]);
          setForm((current) => ({ ...current, images: [...(current.images || []), media.url] }));
        }} /></label>
      </div>
      <label className="block mt-4 text-sm">Content<textarea className="mt-1 w-full border p-2 bg-transparent" rows={8} value={form.content || ""} onChange={set("content")} /></label>
      {preview && <div className="mt-4 border p-4 bg-white" dangerouslySetInnerHTML={{ __html: form.content || "" }} />}
      {error && <p className="mt-3 text-accentRed">{error}</p>}
      <div className="mt-4 flex gap-2">
        <button onClick={save} className="bg-ink text-paper px-4 py-2">Save</button>
        <button onClick={() => setPreview((v) => !v)} className="border border-ink px-4 py-2">Preview</button>
        <button onClick={onCancel} className="px-4 py-2">Cancel</button>
      </div>
    </div>
  );
};

export const MediaLibrary = () => {
  const [items, setItems] = useState([]);
  useEffect(() => { api("/admin/media").then(setItems).catch(() => setItems([])); }, []);
  return <div><h1 className="font-display text-3xl">Media library</h1><div className="mt-6 grid gap-4 md:grid-cols-4">{items.map((item) => <div className="paper p-2" key={item.id}><img src={item.url} alt={item.alt} className="h-32 w-full object-cover" /><p className="mt-2 text-xs break-all">{item.url}</p></div>)}</div></div>;
};

export const Users = () => {
  const [items, setItems] = useState([]);
  useEffect(() => { api("/admin/users").then(setItems).catch(() => setItems([])); }, []);
  return <div><h1 className="font-display text-3xl">Users/admins</h1><div className="mt-6 paper divide-y divide-[var(--paper-edge)]">{items.map((user) => <p className="p-4" key={user.id}>{user.name} · {user.email} · {user.role}</p>)}</div></div>;
};

export default ContentManager;
